// Superadmin only: how sign-ups evolve and every account of the system, with the plan of each and
// the means to block one. The API checks who is a superadmin (SUPERADMIN_EMAILS); the route and the
// menu only hide what would be refused anyway.
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AdminOverview,
  type AdminUser,
  adminOverviewQuery,
  adminUnlinkWordPress,
  adminUsersQuery,
  adminWpConnectionsQuery,
  OVERVIEW_DAYS,
  type OverviewDays,
  PLANS,
  type Plan,
  setUserBanned,
  setUserPlan,
} from '../api';
import { BarChart } from '../chart';
import { Button, ConfirmButton, Field, Message, Page } from '../ui';

const PAGES_PER_MONTH: Record<Plan, number | null> = {
  gratis: 10,
  pro: 1000,
  agencia: 5000,
  'a-medida': 20000,
  infinita: null,
};

type Filter = 'all' | 'unverified' | 'noSite' | 'blocked';
const FILTERS: Filter[] = ['all', 'unverified', 'noSite', 'blocked'];

const selectClass = 'min-h-11 rounded-md border-2 border-field-border bg-paper px-2 text-ink';

export function UsersPage() {
  const { t } = useTranslation();
  return (
    <Page title={t('users.title')} wide>
      <p>{t('users.intro')}</p>
      <Overview />
      <Accounts />
      <WordPressInstallations />
    </Page>
  );
}

/** Sign-ups per day, the figures around them and what became of the accounts. */
function Overview() {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState<OverviewDays>(30);
  const overview = useQuery({ ...adminOverviewQuery(days), placeholderData: keepPreviousData });
  const number = (value: number) => new Intl.NumberFormat(i18n.language).format(value);

  return (
    <section aria-labelledby="altas" className="mt-4 flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 id="altas" className="text-2xl font-bold">
          {t('users.overview.title')}
        </h2>
        <div className="flex flex-col gap-1">
          <label htmlFor="periodo" className="font-bold">
            {t('users.overview.period')}
          </label>
          <select
            id="periodo"
            value={days}
            onChange={(event) => setDays(Number(event.target.value) as OverviewDays)}
            className={selectClass}
          >
            {OVERVIEW_DAYS.map((option) => (
              <option key={option} value={option}>
                {t('users.overview.lastDays', { count: option })}
              </option>
            ))}
          </select>
        </div>
      </div>
      {overview.isPending ? (
        <p>{t('app.loading')}</p>
      ) : overview.isError ? (
        <Message tone="error">{t('errors.generic')}</Message>
      ) : (
        <OverviewData data={overview.data} number={number} />
      )}
    </section>
  );
}

function OverviewData({ data, number }: { data: AdminOverview; number: (value: number) => string }) {
  const { t } = useTranslation();
  const { funnel, signups } = data;
  const change = signups.last7 - signups.previous7;
  const sign = change > 0 ? `+${number(change)}` : change < 0 ? `−${number(-change)}` : '±0';
  const cards: { label: string; value: number; hint?: string }[] = [
    { label: t('users.kpi.accounts'), value: funnel.signedUp },
    {
      label: t('users.kpi.last7'),
      value: signups.last7,
      hint: t('users.kpi.trend', { change: sign, previous: number(signups.previous7) }),
    },
    { label: t('users.kpi.last30'), value: signups.last30 },
    {
      label: t('users.kpi.waitlist'),
      value: data.waitlist.last30,
      hint: t('users.kpi.total', { count: data.waitlist.total }),
    },
    {
      label: t('users.kpi.audits'),
      value: data.freeAudits.last30,
      hint: t('users.kpi.total', { count: data.freeAudits.total }),
    },
    { label: t('users.kpi.blocked'), value: data.banned },
  ];
  const steps = [
    { key: 'signedUp', value: funnel.signedUp },
    { key: 'verified', value: funnel.verified },
    { key: 'organization', value: funnel.withOrganization },
    { key: 'site', value: funnel.withSite },
    { key: 'scan', value: funnel.withScan },
  ];

  return (
    <>
      <dl className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-3">
        {cards.map((card) => (
          <div key={card.label} className="flex flex-col gap-1 rounded-xl border border-line bg-paper p-4">
            <dt className="text-sm font-bold text-ink-muted">{card.label}</dt>
            <dd className="m-0 flex flex-col">
              <span className="text-3xl font-bold">{number(card.value)}</span>
              {card.hint && <span className="text-sm text-ink-muted">{card.hint}</span>}
            </dd>
          </div>
        ))}
      </dl>
      <div className="grid gap-4 md:grid-cols-2">
        <BarChart
          title={t('users.chart.signups')}
          valueLabel={t('users.chart.signupsColumn')}
          points={data.daily.map((day) => ({ date: day.date, value: day.signups }))}
        />
        <BarChart
          title={t('users.chart.waitlist')}
          valueLabel={t('users.chart.waitlistColumn')}
          points={data.daily.map((day) => ({ date: day.date, value: day.waitlist }))}
        />
      </div>
      <section
        aria-labelledby="embudo"
        className="flex flex-col gap-2 rounded-xl border border-line bg-paper p-4"
      >
        <h3 id="embudo" className="text-xl font-bold">
          {t('users.funnel.title')}
        </h3>
        <p className="text-ink-muted">{t('users.funnel.intro')}</p>
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {steps.map((step) => {
            const percent = funnel.signedUp === 0 ? 0 : Math.round((step.value / funnel.signedUp) * 100);
            return (
              <li key={step.key} className="flex flex-col gap-1">
                <div className="flex flex-wrap justify-between gap-2">
                  <span>{t(`users.funnel.${step.key}`)}</span>
                  <strong>{t('users.funnel.value', { count: number(step.value), percent })}</strong>
                </div>
                <div aria-hidden="true" className="h-2 rounded-full bg-brand-tint">
                  <div className="h-2 rounded-full bg-brand" style={{ width: `${percent}%` }} />
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </>
  );
}

/** Every account, with filters, the plan of each and the block button. */
function Accounts() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const users = useQuery(adminUsersQuery);
  const [notice, setNotice] = useState('');
  const [failed, setFailed] = useState<'plan' | 'block' | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const planName = (plan: Plan) => t(`users.plans.${plan}`);
  const limit = (plan: Plan) => {
    const pages = PAGES_PER_MONTH[plan];
    return pages === null ? t('users.limitNone') : t('users.limitMonth', { count: pages });
  };
  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));

  async function save(kind: 'plan' | 'block', action: () => Promise<unknown>, done: string) {
    setFailed(null);
    setNotice('');
    try {
      await action();
    } catch {
      setFailed(kind);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['admin'] });
    setNotice(done);
  }

  const changePlan = (user: AdminUser, plan: Plan) =>
    save(
      'plan',
      () => setUserPlan(user.id, plan),
      t('users.saved', { name: user.name || user.email, plan: planName(plan) }),
    );
  const block = (user: AdminUser, banned: boolean) =>
    save(
      'block',
      () => setUserBanned(user.id, banned),
      t(banned ? 'users.blockedDone' : 'users.unblockedDone', { name: user.name || user.email }),
    );

  const needle = search.trim().toLowerCase();
  const shown = (users.data ?? []).filter(
    (user) =>
      (filter === 'all' ||
        (filter === 'unverified' && !user.emailVerified) ||
        (filter === 'noSite' && user.sites === 0) ||
        (filter === 'blocked' && user.banned)) &&
      (needle === '' ||
        [user.name, user.email, ...user.organizations.map((org) => org.name)].some((text) =>
          text.toLowerCase().includes(needle),
        )),
  );

  return (
    <section aria-labelledby="usuarios" className="team-panel mt-4 flex min-w-0 flex-col gap-4">
      <h2 id="usuarios" className="text-2xl font-bold">
        {users.data ? t('users.count', { count: users.data.length }) : t('users.title')}
      </h2>
      <p role="status" className="empty:hidden">
        {notice}
      </p>
      {failed && <Message tone="error">{t(failed === 'plan' ? 'users.error' : 'users.blockError')}</Message>}
      {users.isPending ? (
        <p>{t('app.loading')}</p>
      ) : users.isError ? (
        <Message tone="error">{t('errors.generic')}</Message>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-60 flex-1">
              <Field label={t('users.filters.search')} type="search" value={search} onChange={setSearch} />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="filtro" className="font-bold">
                {t('users.filters.show')}
              </label>
              <select
                id="filtro"
                value={filter}
                onChange={(event) => setFilter(event.target.value as Filter)}
                className={selectClass}
              >
                {FILTERS.map((option) => (
                  <option key={option} value={option}>
                    {t(`users.filters.${option}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p role="status" className="text-ink-muted">
            {t('users.filters.shown', { count: shown.length, total: users.data.length })}
          </p>
          {/* On a narrow screen the table scrolls: keyboards reach it, and its name says what it holds. `relative`
              keeps the hidden (sr-only) texts of the cells inside the scrolling area. */}
          {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling area must be reachable by keyboard. */}
          <section className="relative overflow-x-auto" aria-label={t('users.tableLabel')} tabIndex={0}>
            <table className="w-full min-w-[72rem] border-collapse text-left">
              <thead>
                <tr className="border-b-2 border-line">
                  {[
                    'user',
                    'verified',
                    'organizations',
                    'signedUp',
                    'sites',
                    'scans',
                    'lastSeen',
                    'status',
                    'plan',
                  ].map((column) => (
                    <th key={column} scope="col" className="py-2 pr-4">
                      {t(`users.columns.${column}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((user) => {
                  const name = user.name || user.email;
                  return (
                    <tr key={user.id} className="border-b border-line align-middle">
                      <td className="min-w-52 py-2 pr-4">
                        <strong className="block wrap-anywhere">{name}</strong>
                        {user.name && <span className="block text-sm wrap-anywhere">{user.email}</span>}
                      </td>
                      <td className="py-2 pr-4">
                        {user.emailVerified ? t('users.verifiedYes') : t('users.verifiedNo')}
                      </td>
                      <td className="py-2 pr-4">
                        {user.organizations.length === 0
                          ? t('users.noOrganizations')
                          : user.organizations.map((org) => org.name).join(', ')}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-4">{date(user.createdAt)}</td>
                      <td className="py-2 pr-4">{user.sites}</td>
                      <td className="py-2 pr-4">{user.scans}</td>
                      <td className="whitespace-nowrap py-2 pr-4">
                        {user.lastSeenAt ? date(user.lastSeenAt) : t('users.neverSeen')}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-col items-start gap-1">
                          <strong>{user.banned ? t('users.blocked') : t('users.active')}</strong>
                          {user.banned && user.banReason && (
                            <span className="text-sm wrap-anywhere">{user.banReason}</span>
                          )}
                          {user.banned ? (
                            <Button variant="secondary" onPress={() => void block(user, false)}>
                              {t('users.unblock')}
                              <span className="sr-only"> · {name}</span>
                            </Button>
                          ) : user.superadmin ? (
                            <span className="text-sm text-ink-muted">{t('users.administrator')}</span>
                          ) : (
                            <ConfirmButton
                              label={t('users.block')}
                              hidden={name}
                              title={t('users.blockTitle', { name })}
                              body={t('users.blockBody')}
                              confirm={t('users.blockConfirm')}
                              cancel={t('users.cancel')}
                              onConfirm={() => block(user, true)}
                            />
                          )}
                        </div>
                      </td>
                      <td className="py-2">
                        <div className="flex flex-col items-start gap-1">
                          <label htmlFor={`tarifa-${user.id}`} className="sr-only">
                            {t('users.planOf', { name })}
                          </label>
                          <select
                            id={`tarifa-${user.id}`}
                            value={user.plan}
                            onChange={(event) => void changePlan(user, event.target.value as Plan)}
                            className={selectClass}
                          >
                            {PLANS.map((plan) => (
                              <option key={plan} value={plan}>
                                {planName(plan)}
                              </option>
                            ))}
                          </select>
                          <span className="text-sm text-ink-muted">
                            {user.plan === 'infinita' ? t('users.infiniteOn') : limit(user.plan)}
                          </span>
                          {user.plan !== 'infinita' && (
                            <Button variant="secondary" onPress={() => void changePlan(user, 'infinita')}>
                              {t('users.infinite')}
                              <span className="sr-only"> · {name}</span>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </>
      )}
    </section>
  );
}

/** Every linked WordPress installation: a superadmin can unlink any of them (the plugin stops at once). */
function WordPressInstallations() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const connections = useQuery(adminWpConnectionsQuery);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState(false);
  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));

  return (
    <section aria-labelledby="wordpress" className="mt-8 flex flex-col gap-4">
      <h2 id="wordpress" className="text-2xl font-bold">
        {t('users.wordpress.title')}
      </h2>
      <p>{t('users.wordpress.intro')}</p>
      <p role="status" className="empty:hidden">
        {notice}
      </p>
      {error && <Message tone="error">{t('errors.generic')}</Message>}
      {connections.isPending ? (
        <p>{t('app.loading')}</p>
      ) : connections.isError ? (
        <Message tone="error">{t('errors.generic')}</Message>
      ) : connections.data.length === 0 ? (
        <p>{t('users.wordpress.empty')}</p>
      ) : (
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling area must be reachable by keyboard.
        <section className="overflow-x-auto" aria-label={t('users.wordpress.tableLabel')} tabIndex={0}>
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b-2 border-line">
                <th scope="col" className="py-2 pr-4">
                  {t('wordpress.list.site')}
                </th>
                <th scope="col" className="py-2 pr-4">
                  {t('users.wordpress.organization')}
                </th>
                <th scope="col" className="py-2 pr-4">
                  {t('wordpress.list.linkedBy')}
                </th>
                <th scope="col" className="py-2 pr-4">
                  {t('wordpress.list.lastSeen')}
                </th>
                <th scope="col" className="py-2">
                  {t('wordpress.list.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {connections.data.map((connection) => (
                <tr key={connection.id} className="border-b border-line align-middle">
                  <td className="py-2 pr-4 wrap-anywhere">{connection.site.url}</td>
                  <td className="py-2 pr-4">{connection.organization}</td>
                  <td className="py-2 pr-4">{connection.authorizedBy}</td>
                  <td className="py-2 pr-4 text-sm">
                    {connection.lastSeenAt ? date(connection.lastSeenAt) : t('wordpress.list.never')}
                  </td>
                  <td className="py-2">
                    <ConfirmButton
                      label={t('wordpress.list.unlink')}
                      hidden={connection.site.url}
                      title={t('wordpress.list.unlinkTitle', { name: connection.site.name })}
                      body={t('users.wordpress.unlinkBody')}
                      confirm={t('wordpress.list.unlinkConfirm')}
                      cancel={t('wordpress.list.cancel')}
                      onConfirm={async () => {
                        setError(false);
                        setNotice('');
                        try {
                          await adminUnlinkWordPress(connection.id);
                          await queryClient.invalidateQueries({ queryKey: ['admin', 'wordpress'] });
                          setNotice(t('wordpress.list.unlinked', { name: connection.site.name }));
                        } catch {
                          setError(true);
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </section>
  );
}
